import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { formatCurrency } from "@/lib/currencyUtils";
import { calculateIngredientCost, getUnitsByType, UNIT_TYPES, UNITS } from "@/lib/unitConversions";
import { normalizeDecimalInput } from "@/lib/numberUtils";
import { Plus, X, Check, ChevronsUpDown, Package } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const RecipeCompositionArea = ({ recipeIngredients, onIngredientsChange, currency, onTotalCostChange }) => {
  const { t, i18n } = useTranslation();
  const [availableIngredients, setAvailableIngredients] = useState([]);
  const [baseRecipes, setBaseRecipes] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [selectedIngredientId, setSelectedIngredientId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [selectedUnit, setSelectedUnit] = useState("g"); // Default unit

  const unitsByType = getUnitsByType();

  useEffect(() => {
    fetchIngredients();
    fetchBaseRecipes();
  }, []);

  // Calculate total cost whenever ingredients or available ingredients change
  useEffect(() => {
    calculateTotalCost();
  }, [recipeIngredients, availableIngredients, baseRecipes]);

  const calculateTotalCost = () => {
    let total = 0;
    recipeIngredients.forEach(recipeIng => {
      let ingredient = availableIngredients.find(i => i.id === recipeIng.ingredient_id);
      let isRecipe = false;
      
      // If not found in ingredients, check base recipes
      if (!ingredient) {
        ingredient = baseRecipes.find(r => r.id === recipeIng.ingredient_id);
        isRecipe = true;
      }
      
      if (ingredient) {
        if (isRecipe) {
          // For base recipes, use total_cost per portion
          const cost = parseFloat(ingredient.total_cost) * parseFloat(recipeIng.quantity);
          total += cost;
        } else {
          // Use unit conversion with density support
          const densityFactor = ingredient.density_factor && parseFloat(ingredient.density_factor) > 0 
            ? parseFloat(ingredient.density_factor) 
            : null;
          
          const cost = calculateIngredientCost(
            recipeIng.quantity,
            recipeIng.unit,
            ingredient.unit_cost,
            ingredient.unit,
            densityFactor  // Golden Thread: density-aware conversion
          );
          total += cost;
        }
      }
    });
    if (onTotalCostChange) {
      onTotalCostChange(total.toFixed(2));
    }
  };

  const fetchIngredients = async () => {
    try {
      const response = await axios.get(`${API}/ingredients`);
      setAvailableIngredients(response.data);
    } catch (error) {
      console.error("Error fetching ingredients:", error);
      toast.error(t("toasts.loadFailed"));
    }
  };

  const fetchBaseRecipes = async () => {
    try {
      const response = await axios.get(`${API}/recipes`);
      const baseRecipesList = response.data.filter(recipe => recipe.is_base_ingredient);
      setBaseRecipes(baseRecipesList);
    } catch (error) {
      console.error("Error fetching base recipes:", error);
    }
  };

  const handleAddIngredient = () => {
    if (!selectedIngredientId || !quantity || parseFloat(quantity) <= 0) {
      toast.error(t("recipeModal.addIngredientError"));
      return;
    }

    // Check if ingredient already added
    if (recipeIngredients.some(i => i.ingredient_id === selectedIngredientId)) {
      toast.error(t("recipeModal.ingredientExists"));
      return;
    }

    // Check if it's a base recipe (use "portion" as unit)
    const isBaseRecipe = baseRecipes.some(r => r.id === selectedIngredientId);
    const unit = isBaseRecipe ? "portion" : selectedUnit;

    const newIngredients = [
      ...recipeIngredients,
      {
        ingredient_id: selectedIngredientId,
        quantity: quantity,
        unit: unit
      }
    ];

    onIngredientsChange(newIngredients);
    setSelectedIngredientId("");
    setQuantity("");
    setSelectedUnit("g"); // Reset to default
    toast.success(t("recipeModal.ingredientAdded"));
  };

  const handleRemoveIngredient = (ingredientId) => {
    const newIngredients = recipeIngredients.filter(i => i.ingredient_id !== ingredientId);
    onIngredientsChange(newIngredients);
  };

  const getIngredientName = (ingredientId) => {
    let item = availableIngredients.find(i => i.id === ingredientId);
    if (!item) {
      item = baseRecipes.find(r => r.id === ingredientId);
      if (item) return `🍽️ ${item.name}`;
    }
    return item ? `${item.name} (${item.unit || 'portion'})` : "Unknown";
  };

  const getIngredientDetails = (ingredientId) => {
    let item = availableIngredients.find(i => i.id === ingredientId);
    if (!item) {
      item = baseRecipes.find(r => r.id === ingredientId);
    }
    return item;
  };

  const getIngredientCost = (ingredientId, qty, unit) => {
    let ingredient = availableIngredients.find(i => i.id === ingredientId);
    let isRecipe = false;
    
    if (!ingredient) {
      ingredient = baseRecipes.find(r => r.id === ingredientId);
      isRecipe = true;
    }
    
    if (!ingredient) return "0.00";
    
    if (isRecipe) {
      // For base recipes, multiply total_cost by quantity
      const cost = parseFloat(ingredient.total_cost) * parseFloat(qty);
      return cost.toFixed(2);
    } else {
      // Use unit conversion with density support
      const densityFactor = ingredient.density_factor && parseFloat(ingredient.density_factor) > 0 
        ? parseFloat(ingredient.density_factor) 
        : null;
      
      const cost = calculateIngredientCost(
        qty,
        unit,
        ingredient.unit_cost,
        ingredient.unit,
        densityFactor  // Golden Thread: density-aware conversion
      );
      return cost.toFixed(2);
    }
  };

  return (
    <div className="space-y-6" data-testid="recipe-composition-area">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-amber-500/10 rounded-lg">
          <Package className="h-6 w-6 text-amber-500" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-white font-manrope">
            {t("recipeModal.ingredients")}
          </h3>
          <p className="text-sm text-slate-400">
            {t("recipeLab.ingredientCount", { count: recipeIngredients.length })}
          </p>
        </div>
      </div>

      {/* Add Ingredient Section */}
      <div className="bg-slate-900/50 rounded-lg border border-slate-800 p-4">
        <Label className="text-slate-300 mb-3 block">
          {t("common.add")} {t("recipeModal.ingredients")}
        </Label>
        
        <div className="flex gap-3">
          {/* Ingredient Selector */}
          <Popover open={searchOpen} onOpenChange={setSearchOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={searchOpen}
                className="flex-1 justify-between bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
                data-testid="ingredient-selector"
              >
                {selectedIngredientId
                  ? getIngredientName(selectedIngredientId)
                  : t("recipeModal.selectIngredient")}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[400px] p-0 bg-slate-800 border-slate-700">
              <Command className="bg-slate-800">
                <CommandInput 
                  placeholder={t("common.search") + "..."} 
                  className="text-white" 
                />
                <CommandEmpty className="text-slate-400 py-6 text-center">
                  {t("recipeModal.noIngredientFound")}
                </CommandEmpty>
                
                {/* Regular Ingredients */}
                {availableIngredients.length > 0 && (
                  <CommandGroup heading={t("recipeModal.ingredients")} className="max-h-64 overflow-y-auto">
                    {availableIngredients.map((ingredient) => (
                      <CommandItem
                        key={ingredient.id}
                        value={ingredient.name}
                        onSelect={() => {
                          setSelectedIngredientId(ingredient.id);
                          setSearchOpen(false);
                        }}
                        className="text-white hover:bg-slate-700 cursor-pointer"
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            selectedIngredientId === ingredient.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <div className="flex-1">
                          <div className="font-medium">{ingredient.name}</div>
                          <div className="text-xs text-slate-400">
                            {formatCurrency(ingredient.unit_cost, currency, 6, i18n.language)}/{ingredient.unit} • {ingredient.category}
                            {ingredient.density_factor && parseFloat(ingredient.density_factor) > 0 && (
                              <span className="ml-2 text-amber-500">
                                🍯 {ingredient.density_factor} g/ml
                              </span>
                            )}
                          </div>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                
                {/* Base Recipes */}
                {baseRecipes.length > 0 && (
                  <CommandGroup heading={t("recipeModal.baseRecipes")} className="max-h-64 overflow-y-auto border-t border-slate-700">
                    {baseRecipes.map((recipe) => (
                      <CommandItem
                        key={recipe.id}
                        value={recipe.name}
                        onSelect={() => {
                          setSelectedIngredientId(recipe.id);
                          setSearchOpen(false);
                        }}
                        className="text-white hover:bg-slate-700 cursor-pointer"
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            selectedIngredientId === recipe.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <div className="flex-1">
                          <div className="font-medium flex items-center gap-2">
                            🍽️ {recipe.name}
                          </div>
                          <div className="text-xs text-slate-400">
                            {formatCurrency(recipe.total_cost, currency, 2, i18n.language)}/portion • {recipe.category}
                          </div>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </Command>
            </PopoverContent>
          </Popover>

          {/* Quantity Input */}
          <Input
            type="number"
            step="0.001"
            value={quantity}
            onChange={(e) => setQuantity(normalizeDecimalInput(e.target.value))}
            placeholder={t("recipeModal.quantity")}
            className="w-32 bg-slate-800 border-slate-700 text-white font-mono"
            data-testid="quantity-input"
          />

          {/* Smart Unit Selector - Density-Aware */}
          {selectedIngredientId && !baseRecipes.find(r => r.id === selectedIngredientId) && (
            <div className="relative">
              {(() => {
                const ingredient = getIngredientDetails(selectedIngredientId);

                // Golden Thread: Dynamic density detection from API payload
                const hasDensity = Boolean(ingredient?.density_factor) &&
                  !Number.isNaN(parseFloat(String(ingredient.density_factor))) &&
                  parseFloat(String(ingredient.density_factor)) !== 0;

                if (hasDensity) {
                  // Show Weight/Volume toggle for density-enabled ingredients
                  const unitInfo = UNITS[selectedUnit] || {};
                  const isVolume = unitInfo.type === UNIT_TYPES.VOLUME;
                  const isWeight = unitInfo.type === UNIT_TYPES.MASS;
                  
                  return (
                    <div className="flex flex-col gap-1">
                      <div className="flex gap-1 bg-slate-800 rounded-lg p-1">
                        <button
                          type="button"
                          onClick={() => setSelectedUnit('kg')}
                          className={cn(
                            "px-3 py-1 rounded text-sm font-medium transition-colors",
                            isWeight ? "bg-amber-500 text-slate-950" : "text-slate-400 hover:text-white"
                          )}
                        >
                          Weight
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedUnit('ml')}
                          className={cn(
                            "px-3 py-1 rounded text-sm font-medium transition-colors",
                            isVolume ? "bg-amber-500 text-slate-950" : "text-slate-400 hover:text-white"
                          )}
                        >
                          Volume
                        </button>
                      </div>
                      <Select value={selectedUnit} onValueChange={setSelectedUnit}>
                        <SelectTrigger 
                          className="w-24 bg-slate-800 border-slate-700 text-white h-8"
                          data-testid="unit-select"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          {isWeight ? (
                            <>
                              <SelectItem value="g" className="text-white">g</SelectItem>
                              <SelectItem value="kg" className="text-white">kg</SelectItem>
                              <SelectItem value="oz" className="text-white">oz</SelectItem>
                              <SelectItem value="lb" className="text-white">lb</SelectItem>
                            </>
                          ) : (
                            <>
                              <SelectItem value="ml" className="text-white">ml</SelectItem>
                              <SelectItem value="L" className="text-white">L</SelectItem>
                              <SelectItem value="cup" className="text-white">cup</SelectItem>
                              <SelectItem value="tbsp" className="text-white">tbsp</SelectItem>
                              <SelectItem value="tsp" className="text-white">tsp</SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                      <div className="text-xs text-amber-500 px-1">
                        🍯 {ingredient.density_factor} g/ml
                      </div>
                    </div>
                  );
                } else {
                  // Regular unit selector for non-density ingredients
                  return (
                    <Select value={selectedUnit} onValueChange={setSelectedUnit}>
                      <SelectTrigger 
                        className="w-28 bg-slate-800 border-slate-700 text-white"
                        data-testid="unit-select"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        {/* Mass Units */}
                        <SelectItem value="g" className="text-white">g</SelectItem>
                        <SelectItem value="kg" className="text-white">kg</SelectItem>
                        <SelectItem value="oz" className="text-white">oz</SelectItem>
                        <SelectItem value="lb" className="text-white">lb</SelectItem>
                        {/* Volume Units */}
                        <SelectItem value="ml" className="text-white">ml</SelectItem>
                        <SelectItem value="L" className="text-white">L</SelectItem>
                        <SelectItem value="fl oz" className="text-white">fl oz</SelectItem>
                        <SelectItem value="cup" className="text-white">cup</SelectItem>
                        <SelectItem value="tbsp" className="text-white">tbsp</SelectItem>
                        <SelectItem value="tsp" className="text-white">tsp</SelectItem>
                        {/* Count Units */}
                        <SelectItem value="each" className="text-white">each</SelectItem>
                        <SelectItem value="piece" className="text-white">piece</SelectItem>
                        <SelectItem value="unit" className="text-white">unit</SelectItem>
                      </SelectContent>
                    </Select>
                  );
                }
              })()}
            </div>
          )}
          {selectedIngredientId && baseRecipes.find(r => r.id === selectedIngredientId) && (
            <div className="w-28 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-300 text-sm text-center">
              portion
            </div>
          )}

          {/* Add Button */}
          <Button
            onClick={handleAddIngredient}
            className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold px-6"
            data-testid="add-ingredient-button"
          >
            <Plus className="h-4 w-4 mr-1" />
            {t("common.add")}
          </Button>

          {/* Real-Time Cost Preview */}
          {selectedIngredientId && quantity && parseFloat(quantity) > 0 && (
            <div className="px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="text-xs text-slate-400">Cost</div>
              <div className="text-lg font-bold text-amber-500 font-mono">
                {formatCurrency(getIngredientCost(selectedIngredientId, quantity, selectedUnit), currency, 2, i18n.language)}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Ingredients List */}
      {recipeIngredients.length === 0 ? (
        <div className="text-center py-12 bg-slate-900/30 rounded-lg border border-slate-800 border-dashed">
          <Package className="h-12 w-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 mb-2">{t("recipeModal.ingredientsRequired")}</p>
          <p className="text-sm text-slate-500">
            {t("recipeModal.selectIngredient")}
          </p>
        </div>
      ) : (
        <div className="bg-slate-900/50 rounded-lg border border-slate-800 divide-y divide-slate-800">
          {recipeIngredients.map((recipeIng) => {
            const ingredient = getIngredientDetails(recipeIng.ingredient_id);
            if (!ingredient) return null;

            return (
              <div
                key={recipeIng.ingredient_id}
                className="p-4 flex items-center justify-between hover:bg-slate-800/30 transition-colors"
                data-testid={`ingredient-item-${recipeIng.ingredient_id}`}
              >
                <div className="flex-1">
                  {/* Ingredient Name */}
                  <div className="font-semibold text-white mb-1">{ingredient.name}</div>
                  
                  {/* Details */}
                  <div className="flex items-center gap-4 text-sm text-slate-400">
                    <span className="flex items-center gap-1">
                      <span className="font-mono">{recipeIng.quantity}</span>
                      <span>{ingredient.unit}</span>
                    </span>
                    <span>×</span>
                    <span className="font-mono">
                      {formatCurrency(ingredient.unit_cost, currency, 6, i18n.language)}/{ingredient.unit}
                    </span>
                  </div>
                </div>

                {/* Cost and Remove */}
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-xs text-slate-500 mb-1">
                      {t("recipeModal.totalCost")}
                    </div>
                    <div className="text-lg font-bold text-amber-500 font-mono">
                      {formatCurrency(getIngredientCost(recipeIng.ingredient_id, recipeIng.quantity), currency, 2, i18n.language)}
                    </div>
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveIngredient(recipeIng.ingredient_id)}
                    className="hover:bg-red-500/10 text-slate-400 hover:text-red-500"
                    data-testid={`remove-ingredient-${recipeIng.ingredient_id}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RecipeCompositionArea;
